import React, { useEffect, useState } from 'react';

import { Icon } from '@column/column-ui-kit';
import { Input, cn } from '@column/ui';

interface ApiKeyTokenInputProps {
  value: string;
  placeholder?: string;
  defaultIsVisible?: boolean;
  // Fetches the token when it isn't loaded yet; resolves false to keep it hidden.
  onReveal?: () => Promise<boolean>;
}

export const ApiKeyTokenInput: React.FC<ApiKeyTokenInputProps> = ({
  value,
  placeholder,
  defaultIsVisible = false,
  onReveal,
}) => {
  const [isVisible, setIsVisible] = useState(defaultIsVisible);
  const [isRevealing, setIsRevealing] = useState(false);
  const canToggle = !!value || !!onReveal;
  const showCopyButton = !!value;

  useEffect(() => {
    if (!value) {
      setIsVisible(defaultIsVisible);
    }
  }, [defaultIsVisible, value]);

  const handleToggle = async () => {
    if (!isVisible && !value && onReveal) {
      setIsRevealing(true);
      const isRevealed = await onReveal().finally(() => setIsRevealing(false));
      if (!isRevealed) {
        return;
      }
    }
    setIsVisible(!isVisible);
  };

  return (
    <div className="relative">
      <Input
        variant="copy"
        readOnly
        autoComplete="off"
        type={isVisible ? 'text' : 'password'}
        value={value}
        placeholder={placeholder}
        showCopyButton={showCopyButton}
        // With the copy button, className lands on the wrapper instead of the input.
        className={
          showCopyButton
            ? cn('[&_input]:font-mono', canToggle && '[&_input]:pl-10')
            : cn('font-mono', canToggle && 'pl-10')
        }
      />
      {canToggle && (
        <button
          type="button"
          aria-label={isVisible ? 'Hide API key' : 'Show API key'}
          disabled={isRevealing}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => void handleToggle()}
          className="group absolute top-1/2 left-2 flex size-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-[4px] text-gray-400 transition-colors outline-none hover:bg-gray-50 hover:text-gray-600 focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 dark:hover:bg-white/10 dark:hover:text-gray-200"
        >
          <Icon.AnimationEye
            line={!isVisible}
            className="[--eye-line-background-color:var(--color-white)]! [--icon-color:currentColor] [--icon-size-x:16px] [--icon-size-y:16px] group-hover:[--eye-line-background-color:var(--color-gray-50)]! dark:[--eye-line-background-color:color-mix(in_oklab,var(--color-input)_30%,var(--color-card))]!"
          />
        </button>
      )}
    </div>
  );
};
